import { GetCountriesListModel } from "@services/models/Common/GetCountriesList";
import {
  AddCpgCommissionRequest,
  AddCpgCommissionResponse,
} from "@services/models/Institution/AddCpgCommissionModel";
import {
  AddInstitutionalRequest,
  AddInstitutionalResponse,
} from "@services/models/Institution/AddInstiutionalModel";
import {
  AddP2PFeesRequest,
  AddP2PFeesResponse,
} from "@services/models/Institution/AddP2PFeesModel";
import { GetBusinessListModel } from "@services/models/Institution/GetBusinessModelList";
import { GetCpgCommisionListResponse } from "@services/models/Institution/GetCpgCommisionListModel";
import {
  GetInstitutionalAccountDataRequest,
  GetInstitutionalAccountDataResponse,
} from "@services/models/Institution/GetInstitutionalAccountDataResponse";
import {
  GetInstitutionalUsersRequest,
  GetInstitutionalUsersResponse,
} from "@services/models/Institution/GetInstiutionalUsersModel";
import { GetLegalStatusListModel } from "@services/models/Institution/GetLegalStatusList";
import { GetP2PFeesResponse } from "@services/models/Institution/GetP2PFeesModel";
import {
  GrantAnalyticsDashboardAccessRequest,
  GrantAnalyticsDashboardAccessResponse,
} from "@services/models/Institution/GrantAnalyticsDashboardAccessModel";
import {
  ActivateInstitutionMerchantConfigRequest,
  ActivateInstitutionMerchantConfigResponse,
  CreateInstitutionMerchantRequest,
  CreateInstitutionMerchantResponse,
  GetInstitutionMerchantConfigRequest,
  GetInstitutionMerchantConfigResponse,
  UpdateInstitutionMerchantConfigRequest,
  UpdateInstitutionMerchantConfigResponse,
} from "@services/models/Institution/InstitutionMerchantConfigModel";
import {
  UpdateInstitutionAccountStatusRequest,
  UpdateInstitutionAccountStatusResponse,
} from "@services/models/Institution/UpdateInstitutionAccountStatusModel";
import { GetCryptoCurrencyListResponse } from "@services/models/Transactions/GetCryptoCurrencyListModel";
import { GetFiatCurrencyListResponse } from "@services/models/Transactions/GetFiatCurrenciesModel";
import { createApiClient } from "../api/createApiClient";
import { AxiosInstance } from "axios";

class InstitutionApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = createApiClient();
  }

  public async getInstitutionalUsers(
    data: GetInstitutionalUsersRequest
  ): Promise<GetInstitutionalUsersResponse> {
    try {
      const response = await this.api.get("/adminPanel/getInstitutionalUsers", {
        params: {
          approvalStatus: data.approvalStatus || undefined,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateInstitutionalAccountStatus(
    data: UpdateInstitutionAccountStatusRequest
  ): Promise<UpdateInstitutionAccountStatusResponse> {
    try {
      const response = await this.api.put(
        "/adminPanel/updateInstitutionalAccountStatus",
        {
          userId: data.userId,
          status: data.status,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getInstituionalAccountDetails(
    data: GetInstitutionalAccountDataRequest
  ): Promise<GetInstitutionalAccountDataResponse> {
    try {
      const response = await this.api.get(
        "/adminPanel/getInstituionalAccountDetails",
        { params: { userId: data.userId } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getLegalStatusList(): Promise<GetLegalStatusListModel> {
    try {
      const response = await this.api.get("/adminPanel/getLegalStatusList");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getBusinessModeList(): Promise<GetBusinessListModel> {
    try {
      const response = await this.api.get("/adminPanel/getBusinessModeList");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async createNewMerchant(
    data: AddInstitutionalRequest
  ): Promise<AddInstitutionalResponse> {
    try {
      const response = await this.api.post("adminPanel/createNewMerchant", {
        ...data,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCountriesList(): Promise<GetCountriesListModel> {
    try {
      const response = await this.api.get("/common/getCountriesList");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCPGCommissionsList(): Promise<GetCpgCommisionListResponse> {
    try {
      const response = await this.api.get("/adminPanel/getCpgCommissionsList");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async addCPGCommission(
    data: AddCpgCommissionRequest
  ): Promise<AddCpgCommissionResponse> {
    try {
      const response = await this.api.post("/adminPanel/addCPGCommission", {
        depositFee: data.depositFee,
        withdrawalFee: data.withdrawalFee,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getP2PFees(): Promise<GetP2PFeesResponse> {
    try {
      const response = await this.api.get("/adminPanel/getP2PFees");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async addP2PFees(
    data: AddP2PFeesRequest
  ): Promise<AddP2PFeesResponse> {
    try {
      const response = await this.api.post("/adminPanel/addP2PFees", {
        p2pCryptoCurrencyId: data.p2pCryptoCurrencyId,
        payInFees: data.payInFees,
        payOutFees: data.payOutFees,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getP2PCryptoCurrencies(): Promise<GetCryptoCurrencyListResponse> {
    try {
      const response = await this.api.get<GetCryptoCurrencyListResponse>(
        "/adminPanel/getP2PCryptoCurrencies"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getP2PFiatCurrencies(): Promise<GetFiatCurrencyListResponse> {
    try {
      const response = await this.api.get<GetFiatCurrencyListResponse>(
        "/adminPanel/getP2PFiatCurrencies"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async grantAnalyticsDashboardAccess(
    data: GrantAnalyticsDashboardAccessRequest
  ): Promise<GrantAnalyticsDashboardAccessResponse> {
    try {
      const response = await this.api.post(
        "/adminPanel/grantAnalyticsDashboardAccess",
        {
          userId: data.userId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async createInstitutionMerchant(
    data: CreateInstitutionMerchantRequest
  ): Promise<CreateInstitutionMerchantResponse> {
    try {
      const response = await this.api.post(
        "/adminPanel/institution/createMerchant",
        {
          userId: data.userId,
          accountCategoryId: data.accountCategoryId,
          ...(data.merchantName ? { merchantName: data.merchantName } : {}),
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async activateInstitutionMerchantConfig(
    data: ActivateInstitutionMerchantConfigRequest
  ): Promise<ActivateInstitutionMerchantConfigResponse> {
    try {
      const response = await this.api.post(
        "/adminPanel/institution/activateMerchantConfig",
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateInstitutionMerchantConfig(
    data: UpdateInstitutionMerchantConfigRequest
  ): Promise<UpdateInstitutionMerchantConfigResponse> {
    try {
      const response = await this.api.put(
        "/adminPanel/institution/updateMerchantConfig",
        {
          merchantId: data.merchantId,
          updates: data.updates,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getInstitutionMerchantConfig(
    data: GetInstitutionMerchantConfigRequest
  ): Promise<GetInstitutionMerchantConfigResponse> {
    try {
      const response = await this.api.get(
        "/adminPanel/institution/getMerchantConfig",
        { params: { merchantId: data.merchantId } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const institutionApis = new InstitutionApiService();
